import { Link } from '@tanstack/react-router';
import type { MaintenanceCard } from '@servwell/shared';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { StatusBadge, Notice } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';
import { IconFleet } from '../icons';
import { dateOnly, dateOnlyMs } from '../../lib/format';
import { dueChip, dueTitle } from './assetVocab';
import { useCompleteMaintenance } from './useCompleteMaintenance';

/**
 * One row of `asset.maintenanceDue`. `daysUntil`, `overdue` and `card` are DERIVED
 * SERVER-SIDE against the shop's clock (107) — this file renders them and never
 * recomputes them.
 */
export type DueRow = {
  id: string;
  assetId: string;
  assetName: string;
  type: string;
  label: string | null;
  dueOn: string;
  notes: string | null;
  daysUntil: number;
  overdue: boolean;
  card: MaintenanceCard;
};

/**
 * What the shop still owes its own vans — the Commusoft anatomy the ticket names: what,
 * which asset, how many days, and a tone that says whether that number is a problem.
 *
 * Client mode, not server mode — every open item is fetched and the sorting, filtering and
 * paging happen in the browser.
 *
 * ⚠️ This is NOT bounded, and an earlier version of this comment claimed it was ("bounded by
 * the fleet, not by how big the business gets") while inventing a population to justify it.
 * `listMaintenanceDue` takes no limit and no per-asset bound, so the row count grows with
 * open maintenance items, not with the number of vans. At the pilot's scale that is tens of
 * rows and client mode is right; it stops being right well before it stops working, and the
 * page will get slow before anything errors.
 *
 * Server mode + a limit is the fix when that day comes, and it is a follow-up on 107's
 * record rather than a silent assumption here.
 *
 * The two cards render as two SEPARATE instances with two `tableId`s rather than one
 * instance whose rows swap — mount-scoped sort, filter and page state would otherwise
 * leak between them, which is the defect 103 found on ReportView.
 */
export function MaintenanceDueTable({
  rows,
  tableId,
  search,
  emptyTitle,
  emptyDescription,
}: {
  rows: DueRow[];
  tableId: string;
  search: string;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const complete = useCompleteMaintenance();

  const columns: DataTableColumn<DueRow>[] = [
    {
      key: 'when',
      label: 'When',
      // Sorted by URGENCY, which is the number, not by the words. The chip is what the
      // reader sees, so `searchValue` is the words — typing "overdue" finds them.
      value: (r) => r.daysUntil,
      searchValue: (r) => dueChip(r).label,
      cell: (r) => {
        const chip = dueChip(r);
        return <StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>;
      },
    },
    {
      key: 'due',
      label: 'Due',
      value: (r) => dateOnlyMs(r.dueOn),
      searchValue: (r) => dateOnly(r.dueOn),
      cell: (r) => dateOnly(r.dueOn),
    },
    {
      key: 'what',
      label: 'What',
      filterable: true,
      value: (r) => dueTitle(r),
      cell: (r) => <span className="font-medium">{dueTitle(r)}</span>,
    },
    {
      key: 'asset',
      label: 'Asset',
      filterable: true,
      value: (r) => r.assetName,
      cell: (r) => (
        <Link
          to="/fleet/assets/$assetId"
          params={{ assetId: r.assetId }}
          className="underline decoration-dotted"
        >
          {r.assetName}
        </Link>
      ),
    },
    {
      key: 'notes',
      label: 'Notes',
      defaultHidden: true,
      value: (r) => r.notes,
      cell: (r) => dash(r.notes),
    },
    {
      key: 'done',
      label: '',
      align: 'right',
      cell: (r) => (
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs"
          disabled={complete.pending}
          onClick={() => complete.markDone(r.id)}
        >
          Mark done
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {complete.error ? <Notice tone="danger">{complete.error.message}</Notice> : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        search={search}
        tableId={tableId}
        empty={
          <EmptyState
            icon={IconFleet}
            title={emptyTitle}
            description={emptyDescription}
          />
        }
      />
    </div>
  );
}
